/**
 * Tower Defense Game
 * Place towers to defend against enemy waves
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const TowerDefense = ({ onHome }) => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState(GAME_CONFIG.states.MENU);
  const [score, setScore] = useLocalStorage('towerdefense_score', 0);
  const [highScore, setHighScore] = useLocalStorage('towerdefense_highScore', 0);
  const [selectedTower, setSelectedTower] = useState(null);

  const gameStateRef = useRef({
    towers: [],
    enemies: [],
    lives: 20,
    gold: 100,
    wave: 1,
    waveCounter: 0,
    enemyCounter: 0,
  });

  const CANVAS_WIDTH = 500;
  const CANVAS_HEIGHT = 400;
  const TOWER_COST = 50;
  const TOWER_RANGE = 80;
  const TOWER_DAMAGE = 10;

  const startGame = () => {
    gameStateRef.current = {
      towers: [],
      enemies: [],
      lives: 20,
      gold: 100,
      wave: 1,
      waveCounter: 0,
      enemyCounter: 0,
    };
    setScore(0);
    setSelectedTower(null);
    setGameState(GAME_CONFIG.states.PLAYING);
  };

  const resetGame = () => {
    if (score > highScore) setHighScore(score);
    setGameState(GAME_CONFIG.states.MENU);
  };

  const handleCanvasClick = (e) => {
    if (gameState !== GAME_CONFIG.states.PLAYING) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const game = gameStateRef.current;

    // Check if tower placement is valid (not on path)
    const pathPoints = [
      { x: 0, y: 50 },
      { x: 120, y: 50 },
      { x: 120, y: 200 },
      { x: 300, y: 200 },
      { x: 300, y: 100 },
      { x: CANVAS_WIDTH, y: 100 },
    ];

    const isOnPath = pathPoints.some((p, idx) => {
      const next = pathPoints[(idx + 1) % pathPoints.length];
      const dx = next.x - p.x;
      const dy = next.y - p.y;
      const t = ((x - p.x) * dx + (y - p.y) * dy) / (dx * dx + dy * dy);
      const distance =
        Math.abs(dy * x - dx * y + next.x * p.y - next.y * p.x) /
        Math.sqrt(dx * dx + dy * dy);
      return t >= 0 && t <= 1 && distance < 40;
    });

    if (!isOnPath && game.gold >= TOWER_COST) {
      game.towers.push({
        x,
        y,
        health: 100,
        shootCounter: 0,
      });
      game.gold -= TOWER_COST;
      setScore((prev) => prev + 10);
    }
  };

  useEffect(() => {
    if (gameState !== GAME_CONFIG.states.PLAYING) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const game = gameStateRef.current;

    const gameLoop = setInterval(() => {
      // Clear canvas
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Draw path
      ctx.strokeStyle = GAME_CONFIG.colors.primary;
      ctx.lineWidth = 40;
      const pathPoints = [
        { x: 0, y: 50 },
        { x: 120, y: 50 },
        { x: 120, y: 200 },
        { x: 300, y: 200 },
        { x: 300, y: 100 },
        { x: CANVAS_WIDTH, y: 100 },
      ];
      ctx.beginPath();
      ctx.moveTo(pathPoints[0].x, pathPoints[0].y);
      pathPoints.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
      ctx.stroke();

      // Spawn enemies
      game.waveCounter++;
      if (game.waveCounter > 60) {
        game.waveCounter = 0;
        game.enemyCounter++;
        if (game.enemyCounter > game.wave + 2) {
          game.enemyCounter = 0;
          game.wave++;
          setScore((prev) => prev + 50);
        }
        game.enemies.push({
          x: 0,
          y: 50,
          pathIndex: 0,
          health: 20,
        });
      }

      // Update enemies
      game.enemies = game.enemies.filter((e) => {
        e.x += 2;
        if (e.x > CANVAS_WIDTH) {
          game.lives--;
          if (game.lives <= 0) {
            setGameState(GAME_CONFIG.states.GAME_OVER);
            if (score > highScore) setHighScore(score);
          }
          return false;
        }
        return true;
      });

      // Tower shooting
      game.towers.forEach((tower) => {
        tower.shootCounter++;
        if (tower.shootCounter > 30) {
          game.enemies.forEach((enemy) => {
            const dx = enemy.x - tower.x;
            const dy = enemy.y - tower.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance < TOWER_RANGE) {
              enemy.health -= TOWER_DAMAGE;
              if (enemy.health <= 0) {
                setScore((prev) => prev + 100);
                game.gold += 20;
              }
              tower.shootCounter = 0;
            }
          });
        }
      });

      // Remove dead enemies
      game.enemies = game.enemies.filter((e) => e.health > 0);

      // Draw towers
      ctx.fillStyle = GAME_CONFIG.colors.accent;
      game.towers.forEach((tower) => {
        ctx.fillRect(tower.x - 15, tower.y - 15, 30, 30);
        ctx.strokeStyle = GAME_CONFIG.colors.accent2;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(tower.x, tower.y, TOWER_RANGE, 0, Math.PI * 2);
        ctx.stroke();
      });

      // Draw enemies
      ctx.fillStyle = GAME_CONFIG.colors.danger;
      game.enemies.forEach((e) => {
        ctx.fillRect(e.x - 10, e.y - 10, 20, 20);
        ctx.fillStyle = '#fff';
        ctx.font = '10px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(Math.ceil(e.health), e.x, e.y + 3);
        ctx.fillStyle = GAME_CONFIG.colors.danger;
      });
    }, 30);

    return () => clearInterval(gameLoop);
  }, [gameState, score, highScore]);

  return (
    <GameLayout
      title="Tower Defense 🏰"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={resetGame}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {gameState === GAME_CONFIG.states.MENU && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Tower Defense</h2>
            <p style={styles.menuDescription}>
              Place towers to defend against enemy waves. Click to place towers (cost: $50).
            </p>
            <button
              onClick={startGame}
              className="btn btn-primary btn-lg"
              style={{ marginTop: '1rem' }}
            >
              🎮 Start Game
            </button>
          </div>
        )}

        {gameState === GAME_CONFIG.states.PLAYING && (
          <>
            <canvas
              ref={canvasRef}
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              onClick={handleCanvasClick}
              style={styles.canvas}
            />
            <div style={styles.stats}>
              <div>Lives: {gameStateRef.current.lives}</div>
              <div>Gold: ${gameStateRef.current.gold}</div>
              <div>Wave: {gameStateRef.current.wave}</div>
            </div>
          </>
        )}

        {gameState === GAME_CONFIG.states.GAME_OVER && (
          <div style={styles.gameOverCard}>
            <h1 style={styles.gameOverTitle}>Game Over</h1>
            <ScoreDisplay score={score} stats={{ Waves: gameStateRef.current.wave }} />
            <button
              onClick={startGame}
              className="btn btn-primary mt-3"
              style={{ width: '100%' }}
            >
              🔄 Play Again
            </button>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    width: '100%',
  },
  menuContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '3rem 2rem',
    textAlign: 'center',
  },
  menuTitle: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '2rem',
    marginBottom: '1rem',
    fontWeight: 'bold',
  },
  menuDescription: {
    color: GAME_CONFIG.colors.light,
  },
  canvas: {
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: '4px',
    backgroundColor: '#000',
    cursor: 'crosshair',
  },
  stats: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.9rem',
    display: 'flex',
    gap: '2rem',
    backgroundColor: `rgba(0, 212, 255, 0.1)`,
    border: `1px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
  },
  gameOverCard: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '2rem',
    textAlign: 'center',
    maxWidth: '400px',
  },
  gameOverTitle: {
    color: GAME_CONFIG.colors.danger,
    marginBottom: '1rem',
    fontSize: '2rem',
  },
};

export default TowerDefense;
